import {Component, Vue} from 'vue-property-decorator';
import {ConvertModule, ICampaign, ICustomerEngagement, IIncentive, TInterval} from '@/stores/modules/convert';
import Axios, {AxiosInstance} from 'axios';

interface ILocationResponse {
	base: string;
	name: string;
	address: string;
	latitude: string;
	longitude: string;
}

interface IBudgetResponse {
	id: string;
	roas: number;
	estimatedDailySales: number;
	order: number;
}

interface ICampaignResponse {
	title: string;
	subtitle: string;
	programId: string;
	enabled: boolean;
	available: boolean;
	budgets: IBudgetResponse[];
	budgetId: null | string;
	created: number;
	modified: number | null;
}

interface IIncentiveCategoryResponse {
	base: string;
	title: string;
	incentives: {
		base: string;
		title: string;
		subtitle: string;
		selected: boolean;
	}[];
}

interface ISalesResponse {
	timestamp: number;
	redemptions: number;
	gross: number;
	net: number;
}

interface IBillingSalesResponse {
	timestamp: number;
	gross: number;
	delivered: number;
	roas: number;
	total: number;
}

interface ISalesSummaryIntervalResponse {
	gross: number;
	net: number;
	timestamp: number;
}

interface ITotalSalesResponse {
	gross: number;
	net: number;
}

interface ICustomerEngagementResponse {
	digitalParticipation: number;
	averageFeedback: number;
}

interface ISalesSummaryResponse {
	day: {
		current: ISalesSummaryIntervalResponse;
		last: ISalesSummaryIntervalResponse;
	};
	week: {
		currentSales: ISalesSummaryIntervalResponse[];
		lastSales: ISalesSummaryIntervalResponse[];
		current: ISalesSummaryIntervalResponse;
		last: ISalesSummaryIntervalResponse;
	};
	month: {
		currentSales: ISalesSummaryIntervalResponse[];
		current: ISalesSummaryIntervalResponse;
		last: ISalesSummaryIntervalResponse;
	};
	year: {
		current: ISalesSummaryIntervalResponse;
	};
}

class Web {
	// private host = 'https://c1.tp-devel.com';
	private basePath = 'admin/squib_automated_marketing.php';
	private axios: AxiosInstance;

	constructor() {
		this.axios = Axios.create({
			baseURL: this.host,
			timeout: 30000,
			withCredentials: true
		});
	}

	get host() {
		if(location.host === 'marketing-beta.tp-qa.com') {
			return 'https://c1b.tp-qa.com';
		} else if(location.host === 'marketing-beta.tchpt.com') {
			return 'https://c1.tchpt.com';
		}
		return 'https://c1.tp-devel.com';
	}

	async getLocations(): Promise<void> {
		const response = await this.axios.get(this.basePath, {
			params: {
				cmd: 'getLocations'
			}
		});
		const serverLocations: ILocationResponse[] = response.data.body.locations;

		ConvertModule.setLocations(serverLocations.map((serverLocation) => {
			return {
				base: serverLocation.base,
				title: serverLocation.name,
				address: serverLocation.address,
				lat: Number(serverLocation.latitude),
				lng: Number(serverLocation.longitude)
			};
		}));
	}

	async getCampaigns(locationBase: string) {
		const response = await this.axios.get(this.basePath, {
			params: {
				cmd: 'getCampaigns',
				locationBase: locationBase
			}
		});
		const serverCampaigns: ICampaignResponse[] = response.data.body;

		ConvertModule.addCampaigns(serverCampaigns.map((campaign) => {
			ConvertModule.addBudgets(campaign.budgets.map((budget) => {
				return {
					id: budget.id,
					locationBase: locationBase,
					programId: campaign.programId,
					estimatedDailySales: budget.estimatedDailySales,
					dailyBudget: Math.round(budget.estimatedDailySales / budget.roas),
					roas: budget.roas,
					order: budget.order
				};
			}));
			return {
				programId: campaign.programId,
				locationBase: locationBase,
				title: campaign.title,
				subtitle: campaign.subtitle,
				enabled: campaign.enabled,
				available: campaign.available,
				budgetId: campaign.budgetId,
				created: campaign.created,
				modified: campaign.modified,
			};
		}));
	}

	async getSalesSummary() {
		const response = await this.axios.get(this.basePath, {
			params: {
				cmd: 'getSalesSummary'
			}
		});

		const serverSalesSummary: ISalesSummaryResponse = response.data.body;
		ConvertModule.setSalesSummary(serverSalesSummary);
	}

	async getTotalSales(locationBase: string | null) {
		const response = await this.axios.get(this.basePath, {
			params: {
				cmd: 'getTotalSales',
				locationBase: locationBase
			}
		});

		const serverTotalSales: ITotalSalesResponse = response.data.body;
		ConvertModule.addTotalSales({
			locationBase: locationBase,
			gross: serverTotalSales.gross,
			net: serverTotalSales.net
		});
	}

	async saveCampaign(campaign: ICampaign) {
		return await this.axios.get(this.basePath, {
			params: {
				cmd: 'saveCampaign',
				locationBase: campaign.locationBase,
				programId: campaign.programId,
				budgetId: campaign.budgetId,
			}
		});
	}

	async disableCampaign(campaign: ICampaign) {
		return await this.axios.get(this.basePath, {
			params: {
				cmd: 'disableCampaign',
				locationBase: campaign.locationBase,
				programId: campaign.programId,
			}
		});
	}

	async getSales(locationBase: string | null, interval: TInterval, startDate: string | null, endDate: string) {
		const response = await this.axios.get(this.basePath, {
			params: {
				cmd: 'getSales',
				locationBase: locationBase,
				interval: interval,
				startDate: startDate,
				endDate: endDate
			}
		});
		const serverSales: ISalesResponse[] = response.data.body;

		ConvertModule.addSales(serverSales.map((serverSale) => {
			return {
				locationBase: locationBase,
				interval: interval,
				timestamp: serverSale.timestamp,
				gross: serverSale.gross,
				net: serverSale.net,
				redemptions: serverSale.redemptions
			};
		}));
	}

	async getBillingSales(locationBase: string | null, interval: TInterval, startDate: string | null, endDate: string) {
		const response = await this.axios.get(this.basePath, {
			params: {
				cmd: 'getBillingSales',
				locationBase: locationBase,
				interval: interval,
				startDate: startDate,
				endDate: endDate
			}
		});
		const serverBillingSales: IBillingSalesResponse[] = response.data.body;

		ConvertModule.addBillingSales(serverBillingSales.map((serverBillingSale) => {
			return {
				locationBase: locationBase,
				interval: interval,
				timestamp: serverBillingSale.timestamp,
				gross: serverBillingSale.gross,
				delivered: serverBillingSale.delivered,
				roas: serverBillingSale.roas,
				total: serverBillingSale.total
			};
		}));
	}

	async getCustomerEngagement(locationBase: string | null = null) {
		const response = await this.axios.get(this.basePath, {
			params: {
				cmd: 'getCustomerEngagement',
				locationBase: locationBase
			}
		});
		const serverCustomerEngagement: ICustomerEngagementResponse = response.data.body;
		ConvertModule.addCustomerEngagement({
			locationBase: locationBase,
			digitalParticipation: serverCustomerEngagement.digitalParticipation,
			averageFeedback: serverCustomerEngagement.averageFeedback
		});
	}

	async downloadBillingSalesPdf(locationBase: string | null, date: string) {
		this.axios.get(this.basePath, {
			responseType: 'blob', // important
			params: {
				cmd: 'getBillingInvoice',
				locationBase: locationBase,
				date: date
			}
		}).then((response) => {
			const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
			// window.open(url);
			const link = document.createElement('a');
			link.style.display = 'none';
			link.href = url;
			link.setAttribute('download', date + '.pdf');
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		});
	}
}


export const WebService = new Web();
